import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SortIcon } from '@common_components/icons/sort.icon';
import { UserUmanforge } from '@umanforge/common';
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';
import { BacklogIcon } from '@common_components/icons/backlog.icon';

interface ParticipantWithPosition extends UserUmanforge {
  position?: number;
}

interface BacklogParticipant {
  id: string;
  fullname: string;
  isBacklog: true;
  position?: number;
}

type OrderableParticipant = ParticipantWithPosition | BacklogParticipant;

interface ParticipantOrderListProps {
  label?: string;
  labelPosition?: 'top' | 'left';
  participants: ParticipantWithPosition[];
  orderedIds: string[];
  setOrderedIds: React.Dispatch<React.SetStateAction<string[]>>;
  eventManagerId?: string;
  backlogName?: string;
  onOrderChange?: ( participantsPositions: { userId: string; position: number }[] ) => void;
  triggerUpdate?: boolean;
  onUpdateComplete?: () => void;
}

const ParticipantOrderList: React.FC<ParticipantOrderListProps> = ( {
  label,
  labelPosition = 'left',
  participants,
  orderedIds,
  setOrderedIds,
  backlogName = DEFAULT_BACKLOG.NAME,
  onOrderChange,
  triggerUpdate,
  onUpdateComplete
} ) => {
  const { t } = useTranslation();
  const [ orderedParticipants, setOrderedParticipants ] = useState<OrderableParticipant[]>( [] );
  const [ draggedItem, setDraggedItem ] = useState<OrderableParticipant | null>( null );
  const [ hasChanges, setHasChanges ] = useState<boolean>( false );
  const [ pendingParticipantsPositions, setPendingParticipantsPositions ] = useState<{ userId: string; position: number }[]>( [] );

  const backlogParticipant: BacklogParticipant = {
    id: DEFAULT_BACKLOG.ID,
    fullname: backlogName === DEFAULT_BACKLOG.NAME ? t( backlogName ) : backlogName,
    isBacklog: true,
    position: participants.find( p => { return p.id === DEFAULT_BACKLOG.ID; } )?.position
  };

  useEffect( () => {

    const participantsMap = new Map<string, ParticipantWithPosition>();
    participants.forEach( participant => {
      participantsMap.set( participant.id, participant );
    } );

    if ( orderedIds.length === 0 && participants.length > 0 ) {
      const sortedParticipants = [ ...participants ].sort( ( a, b ) => {
        const posA = a.position ?? Number.MAX_SAFE_INTEGER;
        const posB = b.position ?? Number.MAX_SAFE_INTEGER;
        return posA - posB;
      } );

      const positions = sortedParticipants
        .map( p => { return p.position; } )
        .filter( pos => { return pos !== undefined && pos !== Number.MAX_SAFE_INTEGER; } ) as number[];

      const existingBacklogParticipant = participants.find( p => { return p.id === DEFAULT_BACKLOG.ID; } );
      let backlogPosition = 0;

      if ( existingBacklogParticipant && existingBacklogParticipant.position !== undefined ) {
        backlogPosition = existingBacklogParticipant.position;
      } else if ( positions.length === 0 ) {
        backlogPosition = Math.floor( participants.length / 2 );
      } else {
        const sortedPositions = positions.sort( ( a, b ) => { return a - b; } );

        // Chercher le premier trou dans les positions
        backlogPosition = sortedPositions.length;
        for ( let i = 0; i < sortedPositions.length; i++ ) {
          if ( sortedPositions[ i ] !== i ) {
            backlogPosition = i;
            break;
          }
        }
      }


      const orderedWithBacklog: OrderableParticipant[] = [];

      if ( positions.length === 0 ) {
        const maxPosition = participants.length;

        for ( let pos = 0; pos <= maxPosition; pos++ ) {
          if ( pos === backlogPosition ) {
            orderedWithBacklog.push( backlogParticipant );
          } else {
            const participantIndex = pos > backlogPosition ? pos - 1 : pos;
            if ( participantIndex < sortedParticipants.length ) {
              orderedWithBacklog.push( sortedParticipants[ participantIndex ] );
            }
          }
        }
      } else {
        const sortedPositions = positions.sort( ( a, b ) => { return a - b; } );
        const maxParticipantPosition = positions.length > 0 ? Math.max( ...sortedPositions ) : -1;
        const totalPositions = Math.max( maxParticipantPosition + 1, backlogPosition + 1 );

        for ( let pos = 0; pos < totalPositions; pos++ ) {
          if ( pos === backlogPosition ) {
            orderedWithBacklog.push( backlogParticipant );
          } else {
            const participantAtPosition = sortedParticipants.find( p => { return p.position === pos; } );
            if ( participantAtPosition ) {
              orderedWithBacklog.push( participantAtPosition );
            }
          }
        }
      }
      setOrderedParticipants( orderedWithBacklog );
      setOrderedIds( orderedWithBacklog.map( p => { return p.id; } ) );
      return;
    }

    const ordered: OrderableParticipant[] = [];

    orderedIds.forEach( id => {
      if ( id === DEFAULT_BACKLOG.ID ) {
        ordered.push( backlogParticipant );
      } else {
        const participant = participantsMap.get( id );
        if ( participant ) {
          ordered.push( participant );
        }
      }
    } );

    if ( !ordered.find( p => { return p.id === DEFAULT_BACKLOG.ID; } ) ) {
      ordered.push( backlogParticipant );
    }

    setOrderedParticipants( ordered );
  }, [ participants, orderedIds, setOrderedIds, backlogName, t ] );

  useEffect( () => {
    if ( triggerUpdate && hasChanges && onOrderChange ) {
      onOrderChange( pendingParticipantsPositions );
      setHasChanges( false );
      setPendingParticipantsPositions( [] );
      if ( onUpdateComplete ) {
        onUpdateComplete();
      }
    }
  }, [ triggerUpdate, hasChanges, onOrderChange, pendingParticipantsPositions, onUpdateComplete ] );

  const handleDragStart = ( e: React.DragEvent<HTMLDivElement>, participant: OrderableParticipant ) => {
    setDraggedItem( participant );
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData( 'text/plain', participant.id );
  };

  const handleDragOver = ( e: React.DragEvent<HTMLDivElement> ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = ( e: React.DragEvent<HTMLDivElement>, targetParticipant: OrderableParticipant ) => {
    e.preventDefault();

    if ( !draggedItem || draggedItem.id === targetParticipant.id ) {
      return;
    }

    const currentItems = [ ...orderedParticipants ];
    const draggedIndex = currentItems.findIndex( p => { return p.id === draggedItem.id; } );
    const targetIndex = currentItems.findIndex( p => { return p.id === targetParticipant.id; } );
    if ( draggedIndex === -1 || targetIndex === -1 ) {
      return;
    }

    const [ removed ] = currentItems.splice( draggedIndex, 1 );
    currentItems.splice( targetIndex, 0, removed );

    setOrderedParticipants( currentItems );

    const newOrderIds = currentItems.map( p => { return p.id; } );
    setOrderedIds( newOrderIds );

    const participantsPositions: { userId: string; position: number }[] = [];

    currentItems.forEach( ( item, index ) => {
      if ( item.id !== DEFAULT_BACKLOG.ID ) {
        participantsPositions.push( {
          userId: item.id,
          position: index
        } );
      }
    } );

    setPendingParticipantsPositions( participantsPositions );
    setHasChanges( true );
  };

  const handleDragEnd = () => {
    setDraggedItem( null );
  };

  return (
    <div className={`flex ${labelPosition === 'top' ? 'flex-col gap-2' : 'items-start gap-5'}`}>
      {label && (
        <div className={`flex items-center ${labelPosition === 'left' ? 'w-1/3' : ''}`}>
          <SortIcon size={7} className="mr-2 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
      )}

      <div className={`w-full ${labelPosition === 'left' && label ? 'w-2/3' : ''}`}>
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h3 className="text-sm font-medium text-gray-700">
              {t( 'translation:drag_to_reorder_participants' )}
            </h3>
          </div>

          <div className="p-2 bg-white">
            {orderedParticipants.length === 0 ? (
              <p className="text-gray-500 text-sm p-2">
                {t( 'translation:no_participants_selected' )}
              </p>
            ) : (
              <div className="space-y-1">
                {orderedParticipants.map( ( participant, index ) => {
                  return (
                    <div
                      key={participant.id}
                      draggable
                      onDragStart={e => { return handleDragStart( e, participant ); }}
                      onDragOver={handleDragOver}
                      onDrop={e => { return handleDrop( e, participant ); }}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center p-2 rounded-md cursor-move hover:bg-gray-50 ${
                        draggedItem?.id === participant.id ? 'opacity-50 bg-gray-100' : ''
                      }`}
                    >
                      <div className="p-1 mr-2 text-gray-400 flex items-center">
                        <span className="mr-2 text-xs text-gray-500 font-semibold">
                          { `#${index + 1}` }
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18L15 12L9 6" />
                        </svg>
                      </div>
                      <div className="flex items-center flex-1">
                        {'isBacklog' in participant && participant.isBacklog ? (
                          <div className="flex items-center text-sm">
                            <BacklogIcon size={4} className="mr-2 text-blue-500" />
                            <span className="text-blue-700 font-medium">{participant.fullname}</span>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-700">
                            {participant.fullname || ( 'firstname' in participant && participant.firstname && 'lastname' in participant && participant.lastname ? `${participant.firstname} ${participant.lastname}` : 'Utilisateur' )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantOrderList;
